import { ThemeEvents } from '@theme/events';

/**
 * Keeps variant-metafield content in sync with Horizon's section response.
 */
class AceVariantMetafieldElement extends HTMLElement {
  connectedCallback() {
    this.variantEventTarget = this.eventTarget;
    this.variantEventTarget?.addEventListener(ThemeEvents.variantUpdate, this.updateFromVariant);
  }

  disconnectedCallback() {
    this.variantEventTarget?.removeEventListener(ThemeEvents.variantUpdate, this.updateFromVariant);
  }

  get eventTarget() {
    return this.closest('[id*="ProductInformation-"], .shopify-section, dialog');
  }

  /**
   * Applies the new value from the element's own variant-id keyed map.
   *
   * Preferred over the section-HTML path below, which silently gives up whenever
   * the response does not carry this element — partial section renders from quick
   * add or product cards, and combined listings where the picker reports a
   * different product id than this element. Those cases left a stale value on
   * screen with nothing logged.
   *
   * A variant id that is absent from the map belongs to another product, so the
   * caller falls through to the guarded HTML path rather than touching the DOM.
   *
   * @param {CustomEvent} event - The variant update event.
   * @returns {boolean} Whether the value was applied.
   */
  updateFromValueMap(event) {
    const source = this.dataset.variantValues;
    const variantId = event.detail?.resource?.id;
    if (!source || variantId == null) return false;

    let values;
    try {
      values = JSON.parse(source);
    } catch {
      return false;
    }

    const value = values[String(variantId)];
    if (typeof value !== 'string') return false;

    const target = this.querySelector('[data-variant-value]');
    const row = this.querySelector('[data-variant-value-row]');
    if (!(target instanceof HTMLElement) || !(row instanceof HTMLElement)) return false;

    target.textContent = value;
    row.hidden = value === '';
    return true;
  }

  updateFromVariant = (event) => {
    if (this.updateFromValueMap(event)) return;

    if (!event.detail.data.newProduct && event.target instanceof HTMLElement) {
      if (event.target.dataset.productId !== this.dataset.productId) return;
    }

    const html = event.detail.data.html;
    if (!html || typeof html.querySelectorAll !== 'function') return;

    const replacement = Array.from(html.querySelectorAll(this.localName)).find(
      (element) => element.dataset.blockId === this.dataset.blockId
    );

    if (!replacement) return;

    this.dataset.productId = replacement.dataset.productId || this.dataset.productId;
    // Combined listings swap in a different product, so carry its value map over
    // as well — replaceChildren only touches children, not attributes.
    if (replacement.dataset.variantValues) {
      this.dataset.variantValues = replacement.dataset.variantValues;
    }
    this.replaceChildren(...Array.from(replacement.childNodes).map((node) => node.cloneNode(true)));
  };
}

// The registry allows each constructor only once — registering the shared base
// class under both names throws NotSupportedError on the second define, leaving
// ace-variant-sku unregistered (and its content frozen). Each name needs its
// own subclass.
class AceVariantTitleElement extends AceVariantMetafieldElement {}
class AceVariantSkuElement extends AceVariantMetafieldElement {}

if (!customElements.get('ace-variant-title')) {
  customElements.define('ace-variant-title', AceVariantTitleElement);
}

if (!customElements.get('ace-variant-sku')) {
  customElements.define('ace-variant-sku', AceVariantSkuElement);
}
